// utils.js

// Priority investor types
const priorityInvestorTypes = [
    'Venture Capital',
    'Private Equity Firm',
    'Micro VC',
    'Individual/Angel',
    'Family Investment Office',
    'CEO/Former CEO',
    'Angel Group'
  ];

export const DEFAULT_INVESTOR_FILTERS = {
  selectedTypes: [],
  searchKeyword: '',
  searchCountry: '',
  industries: [],
  onlyWithInvestmentsInfo: false,
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeString(item))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const looksLikeLegacyInvestorTypeCodes = (values) =>
  values.length > 0 && values.every((value) => /^[a-z0-9]+$/.test(value));

export const normalizeInvestorFilters = (filters = {}) => ({
  selectedTypes: normalizeArray(filters.selectedTypes),
  searchKeyword: normalizeString(filters.searchKeyword || filters.keyword),
  searchCountry: normalizeString(
    filters.searchCountry || filters.mainOfficeLocation || filters.country
  ),
  industries: normalizeArray(filters.industries || filters.industry),
  onlyWithInvestmentsInfo: Boolean(filters.onlyWithInvestmentsInfo),
});

export const readPersistedInvestorFilters = (search = '') => {
  let nextFilters = DEFAULT_INVESTOR_FILTERS;

  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem('investorsFilters');
      nextFilters = stored
        ? normalizeInvestorFilters(JSON.parse(stored))
        : DEFAULT_INVESTOR_FILTERS;
    } catch (error) {
      console.error('Error reading persisted investor filters:', error);
    }
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const investorTypesFromUrl = normalizeArray(params.get('investorTypes'));

  if (
    investorTypesFromUrl.length > 0 &&
    !looksLikeLegacyInvestorTypeCodes(investorTypesFromUrl)
  ) {
    nextFilters = {
      ...nextFilters,
      selectedTypes: investorTypesFromUrl,
    };
  }

  if (params.has('country')) {
    nextFilters = {
      ...nextFilters,
      searchCountry: normalizeString(params.get('country')),
    };
  }

  if (params.has('industries')) {
    nextFilters = {
      ...nextFilters,
      industries: normalizeArray(params.get('industries')),
    };
  }

  if (params.has('keyword')) {
    nextFilters = {
      ...nextFilters,
      searchKeyword: normalizeString(params.get('keyword')),
    };
  }

  if (params.has('onlyWithInvestmentsInfo')) {
    nextFilters = {
      ...nextFilters,
      onlyWithInvestmentsInfo:
        params.get('onlyWithInvestmentsInfo') === 'true' ||
        params.get('onlyWithInvestmentsInfo') === '1',
    };
  }

  return normalizeInvestorFilters(nextFilters);
};

export const readLegacyInvestorTypeCodes = (search = '') => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const investorTypes = normalizeArray(params.get('investorTypes'));
  return looksLikeLegacyInvestorTypeCodes(investorTypes) ? investorTypes : [];
};

export const persistInvestorFilters = (filters) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      'investorsFilters',
      JSON.stringify(normalizeInvestorFilters(filters))
    );
  } catch (error) {
    console.error('Error persisting investor filters:', error);
  }
};
  
  export const sortInvestorTypes = (types, sortType) => {
    if (sortType === 'alphabetical') {
      return [...types].sort(); // Alphabetical sorting
    }
  
    // Sort by priority
    const sortedTypes = [...types].sort((a, b) => {
      const aIsPriority = priorityInvestorTypes.includes(a);
      const bIsPriority = priorityInvestorTypes.includes(b);
  
      if (aIsPriority && !bIsPriority) {
        return -1; // Priority types come first
      }
      if (!aIsPriority && bIsPriority) {
        return 1;
      }
      return a.localeCompare(b); // Remaining types are sorted alphabetically
    });
  
    return sortedTypes;
  };
  
  export const triggerFilterChange = (filters, onFilterChange, resetPage = true) => {
    if (onFilterChange) {
      onFilterChange(
        {
          mainOfficeLocation: filters.searchCountry,
          selectedTypes: filters.selectedTypes,
          industry: filters.industries[0],
          keyword: filters.searchKeyword,
          onlyWithInvestmentsInfo: filters.onlyWithInvestmentsInfo,
        },
        resetPage
      );
    }
  };
  
